import React, { useEffect } from 'react';

import { useAtom, useAtomValue } from 'jotai';
import { 
    isSeparateDisplayControllerAtom, 
    subMapAtom, 
    mainMapAtom, 
    selectedBaseMapAtom, 
    selectedBaseMapLeftAtom, 
    isSidebarOpenAtom, 
    selectedSubMapLayersAtom, 
    selectedLayersAtom, 
    mapClickEventAtom, 
    leftMapClickEventAtom, 
    dataErrorMessageAtom, 
    leftMapDataErrorMessageAtom, 
    nonLngLatDataArrAtom, 
    leftMapNonLngLatDataArrAtom, 
    gyoseikaiObjAtom, 
    isCenterMarkControlOpenAtom, 
    isIndexMapOpenAtom, 
    isLeftCenterMarkControlOpenAtom, 
    isLeftIndexMapOpenAtom 
} from '../../atoms';
import SearchBar from '../searchbar/SearchBar';
import Map from './Map';
import { MAIN_MAP_ID, SECOND_MAP_ID } from '../../constants/components/map';
import './MapContainer.css';
import { SetURLSearchParams } from 'react-router-dom';
import { AppSettingsType } from '../../types/lib/types';
import classNames from 'classnames';
import ThemeMaps from '../themeMaps/ThemeMapList';


export type MapContainerProps = {
    isDesktop: boolean;
    setSearchParams: SetURLSearchParams;
    searchParams: URLSearchParams;
    appSettings: AppSettingsType;
}

const MapContainer: React.FC<MapContainerProps> = ({ appSettings }) => {

    const isSeparateDisplay = useAtomValue(isSeparateDisplayControllerAtom);                    // 2画面表示フラグ
    const gyoseikaiObj = useAtomValue(gyoseikaiObjAtom);                                        // 行政界のオブジェクト

    // map objects
    const [mainMap, setMainMap] = useAtom(mainMapAtom);                                         // メインマップ
    const [subMap, setSubMap] = useAtom(subMapAtom);                                            // サブマップ

    // sidebar open/close
    const isSidebarOpen = useAtomValue(isSidebarOpenAtom);                                      // サイドバー開け閉め（PC）

    // map background
    const [selectedBaseMap, setSelectedBaseMap] = useAtom(selectedBaseMapAtom);                 // 選択中のベースマップ（メイン）
    const [selectedBaseMapLeft, setSelectedBaseMapLeft] = useAtom(selectedBaseMapLeftAtom);     // 選択中のベースマップ（左：サブ）

    // selected layers
    const selectedLayers = useAtomValue(selectedLayersAtom); 
    const [selectedSubMapLayers, setSelectedSubMapLayers] = useAtom(selectedSubMapLayersAtom);

    // click event
    const [mapClickEvent, setMapClickEvent] = useAtom(mapClickEventAtom);
    const [leftMapClickEvent, setLeftMapClickEvent] = useAtom(leftMapClickEventAtom);

    // error data
    const [dataErrorMessage, setDataErrorMessage] = useAtom(dataErrorMessageAtom);
    const [leftMapDataErrorMessage, setLeftMapDataErrorMessage] = useAtom(leftMapDataErrorMessageAtom);

    // non lnglat data
    const [nonLngLatDataArr, setNonLngLatDataArr] = useAtom(nonLngLatDataArrAtom);
    const [leftMapNonLngLatDataArr, setLeftMapNonLngLatDataArr] = useAtom(leftMapNonLngLatDataArrAtom);

    // center mark
    const [isCenterMarkControlOpen, setIsCenterMarkControlOpen] = useAtom(isCenterMarkControlOpenAtom);
    const [isLeftCenterMarkControlOpen, setIsLeftCenterMarkControlOpen] = useAtom(isLeftCenterMarkControlOpenAtom);

    // index map
    const [isIndexMapOpen, setIsIndexMapOpen] = useAtom(isIndexMapOpenAtom);
    const [isLeftIndexMapOpen, setLeftIsIndexMapOpen] = useAtom(isLeftIndexMapOpenAtom);


    useEffect(() => {
        if(isSeparateDisplay) { 
            setSelectedSubMapLayers([gyoseikaiObj?.id??'']);
            return; 
        }
        subMap?.remove();
        setSubMap(null);
    }, [isSeparateDisplay, setSubMap, subMap, gyoseikaiObj]);


    /* ======================= */

    return (
        <div className={classNames('map-container', {'map-width-full': !isSidebarOpen})}>
            {/* 検索 */}
            { !isSeparateDisplay &&
                <>
                    <ThemeMaps showMoreButton={true} />
                    <div className='searchbar-container'>
                        <SearchBar />
                    </div>
                </>
            }

            { /* マップ */ }
            <Map 
                mapObj={mainMap}
                setMapObj={setMainMap}
                mapType={MAIN_MAP_ID}
                selectedBaseMap={selectedBaseMap}
                setSelectedBaseMap={setSelectedBaseMap}
                appSettings={appSettings}
                selectedLayers={selectedLayers}
                clickEvent={mapClickEvent}
                setClickEvent={setMapClickEvent}
                dataErrorMessage={dataErrorMessage}
                setDataErrorMessage={setDataErrorMessage}
                nonLngLatDataArr={nonLngLatDataArr}
                setNonLngLatDataArr={setNonLngLatDataArr}
                isIndexMapOpen={isIndexMapOpen}
                setIsIndexMapOpen={() => setIsIndexMapOpen(prev => !prev)}
                isCenterMarkControlOpen={isCenterMarkControlOpen}
                setIsCenterMarkControlOpen={() => setIsCenterMarkControlOpen(prev => !prev)}
            />
            { isSeparateDisplay &&
                <Map 
                    mapObj={subMap}
                    setMapObj={setSubMap}
                    mapType={SECOND_MAP_ID}
                    selectedBaseMap={selectedBaseMapLeft}
                    setSelectedBaseMap={setSelectedBaseMapLeft}
                    appSettings={appSettings}
                    selectedLayers={selectedSubMapLayers}
                    clickEvent={leftMapClickEvent}
                    setClickEvent={setLeftMapClickEvent}
                    dataErrorMessage={leftMapDataErrorMessage}
                    setDataErrorMessage={setLeftMapDataErrorMessage}
                    nonLngLatDataArr={leftMapNonLngLatDataArr}
                    setNonLngLatDataArr={setLeftMapNonLngLatDataArr}
                    isIndexMapOpen={isLeftIndexMapOpen}
                    setIsIndexMapOpen={() => setLeftIsIndexMapOpen(prev => !prev)}
                    isCenterMarkControlOpen={isLeftCenterMarkControlOpen}
                    setIsCenterMarkControlOpen={() => setIsLeftCenterMarkControlOpen(prev => !prev)}
                /> 
            }
        </div>
    );
};

export default MapContainer;
